'use client'

import type { ComponentProps, ReactNode } from 'react'

import { CaretDownIcon, UserIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { MetricBadgeCount } from '@/components/ui/metrics'
import { PostEngagement } from '@/components/ui/post-engagement'
import { UserMessengerIcon } from '@/components/icons/goodunited-icons'

/*
 * GoodUnited / Source accordion. Figma: 11 / Components / Accordion.
 * Compose SourceList -> Source -> Group -> DetailRow.
 * The user badge counts supporters, the Messenger badge counts subscribers. Hide either badge when it does not apply.
 * Source rows sit on Colors/surface/raised, groups on Colors/surface/subtle, detail rows on Colors/surface/muted.
 */
type SourceAccent = 'facebook' | 'messenger' | 'platform' | 'challenges' | 'instagram' | 'other' | 'birthdays' | 'misc'

const accentClasses: Record<SourceAccent, string> = {
    facebook: 'bg-chart-facebook',
    messenger: 'bg-chart-messenger',
    platform: 'bg-chart-platform',
    challenges: 'bg-chart-challenges',
    instagram: 'bg-chart-instagram',
    other: 'bg-chart-other',
    birthdays: 'bg-chart-birthdays',
    misc: 'bg-chart-misc'
}

interface CountValue {
    value: string
    detail?: string
}

interface SourceCountsProps {
    supporters?: string | CountValue
    subscribers?: CountValue
}

/* Count badges: supporters (person icon, optional share) and subscribers (Messenger icon, optional share). */
function SourceCounts({ supporters, subscribers }: SourceCountsProps) {
    const people = typeof supporters === 'string' ? { value: supporters } : supporters

    return (
        <span className='flex shrink-0 items-center gap-2'>
            {people !== undefined && (
                <MetricBadgeCount
                    value={people.value}
                    detail={people.detail}
                    icon={<UserIcon className='text-icon-secondary' aria-hidden='true' />}
                    aria-label={`${people.value} supporters${people.detail ? `, ${people.detail}` : ''}`}
                />
            )}
            {subscribers !== undefined && (
                <MetricBadgeCount
                    value={subscribers.value}
                    detail={subscribers.detail}
                    icon={<UserMessengerIcon className='text-external-messenger' />}
                    aria-label={`${subscribers.value} subscribers${subscribers.detail ? `, ${subscribers.detail}` : ''}`}
                />
            )}
        </span>
    )
}

interface DetailRowData extends SourceCountsProps {
    label: string
    engagement?: { views: string, likes: string, comments: string }
}

interface GroupData extends SourceCountsProps {
    id: string
    label: string
    rows?: DetailRowData[]
}

interface SourceData extends SourceCountsProps {
    id: string
    label: string
    accent: SourceAccent
    icon: ReactNode
    groups?: GroupData[]
}

/* Accordion/DetailRow */
function SourceDetailRow({ label, engagement, supporters, subscribers, className }: DetailRowData & { className?: string }) {
    return (
        <div data-slot='source-detail-row' className={cn('flex items-center justify-between gap-3 border-b border-border-subtle bg-surface-muted py-2.5 pr-4 pl-12 last:border-b-0', className)}>
            <div className='flex min-w-0 flex-col gap-1'>
                <span className='truncate text-base text-text-primary'>
                    {label}
                </span>
                {engagement && <PostEngagement {...engagement} />}
            </div>
            <SourceCounts supporters={supporters} subscribers={subscribers} />
        </div>
    )
}

/* Accordion/GroupTrigger + Accordion/Group. A group without rows renders as a plain item. */
function SourceGroup({ group }: { group: GroupData }) {
    if (!group.rows?.length) {
        return (
            <div data-slot='source-item' className='flex min-h-14 items-center justify-between gap-3 border-b border-border-subtle bg-surface-subtle px-4 py-3'>
                <span className='min-w-0 truncate text-base text-text-primary'>
                    {group.label}
                </span>
                <SourceCounts supporters={group.supporters} subscribers={group.subscribers} />
            </div>
        )
    }

    return (
        <AccordionItem value={group.id} className='border-b border-border-subtle'>
            <AccordionTrigger className='min-h-14 justify-between gap-3 bg-surface-subtle px-4 py-3'>
                <span className='min-w-0 truncate text-base text-text-primary'>
                    {group.label}
                </span>
                <span className='flex items-center gap-3'>
                    <SourceCounts supporters={group.supporters} subscribers={group.subscribers} />
                    <CaretDownIcon className='size-4 text-icon-secondary transition-transform group-data-panel-open/accordion-trigger:rotate-180' aria-hidden='true' />
                </span>
            </AccordionTrigger>
            <AccordionContent>
                {group.rows.map(row => <SourceDetailRow key={row.label} {...row} />)}
            </AccordionContent>
        </AccordionItem>
    )
}

/* Accordion/SourceTrigger + Accordion/Source */
function SourceItem({ source }: { source: SourceData }) {
    return (
        <AccordionItem value={source.id} className='border-b border-border-subtle bg-surface-raised last:border-b-0'>
            <AccordionTrigger className='min-h-14 justify-between gap-3 px-4 py-3'>
                <span className='flex min-w-0 items-center gap-2'>
                    <span className={cn('h-6 w-2 shrink-0 rounded-mark', accentClasses[source.accent])} aria-hidden='true' />
                    <span className='inline-flex shrink-0 text-icon-secondary [&_svg]:size-5' aria-hidden='true'>
                        {source.icon}
                    </span>
                    <span className='truncate text-base font-medium text-text-primary'>
                        {source.label}
                    </span>
                </span>
                <span className='flex items-center gap-3'>
                    <SourceCounts supporters={source.supporters} subscribers={source.subscribers} />
                    <CaretDownIcon className='size-4 text-icon-secondary transition-transform group-data-panel-open/accordion-trigger:rotate-180' aria-hidden='true' />
                </span>
            </AccordionTrigger>
            {source.groups && source.groups.length > 0 && (
                <AccordionContent>
                    <Accordion multiple>
                        {source.groups.map(group => <SourceGroup key={group.id} group={group} />)}
                    </Accordion>
                </AccordionContent>
            )}
        </AccordionItem>
    )
}

interface SourceListProps extends Omit<ComponentProps<'div'>, 'defaultValue'> {
    sources: SourceData[]
    defaultOpen?: string[]
}

/* Accordion/SourceList: ready for a drawer or a page container. Sources and nested groups toggle independently. */
function SourceList({ sources, defaultOpen = [], className, ...props }: SourceListProps) {
    return (
        <div data-slot='source-list' className={cn('overflow-hidden rounded-lg border border-border-subtle bg-surface-raised', className)} {...props}>
            <Accordion multiple defaultValue={defaultOpen}>
                {sources.map(source => <SourceItem key={source.id} source={source} />)}
            </Accordion>
        </div>
    )
}

export { SourceCounts, SourceDetailRow, SourceList }
export type { DetailRowData, GroupData, SourceAccent, SourceData }
